import { useState, useEffect } from "react";
import { api } from "../api";
import {
  UploadCloud,
  FileSpreadsheet,
  Trash2,
  CheckCircle2,
  Loader2,
  AlertCircle,
  TriangleAlert,
  FileText,
} from "lucide-react";

export default function UploadPage({ embedded = false }: { embedded?: boolean }) {
  const [specialty, setSpecialty] = useState("All");
  const [reportType, setReportType] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error" | "warning"; text: string } | null>(null);
  const [warningUploadId, setWarningUploadId] = useState<number | null>(null);
  const [markdownView, setMarkdownView] = useState<{ id: number; filename: string; text: string } | null>(null);
  const [uploads, setUploads] = useState<any[]>([]);

  const loadUploads = () => {
    api.listUploads().then(setUploads).catch(() => {});
  };

  useEffect(() => {
    loadUploads();
  }, []);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setMessage(null);
    try {
      const result = await api.upload(file, specialty, reportType || undefined);
      if (result.validation_warning) {
        setMessage({
          type: "warning",
          text: `Parsed ${result.row_count} records, but validation warning: ${result.validation_warning}`,
        });
        setWarningUploadId(result.id);
      } else {
        setMessage({
          type: "success",
          text: `Uploaded successfully! ${result.row_count} records parsed. Report: ${result.report_year}/${result.report_month?.toString().padStart(2, "0")}`,
        });
        setWarningUploadId(null);
      }
      setFile(null);
      loadUploads();
    } catch (e) {
      setMessage({
        type: "error",
        text: e instanceof Error ? e.message : "Upload failed",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this upload and all its data?")) return;
    try {
      await api.deleteUpload(id);
      loadUploads();
    } catch (e) {
      setMessage({
        type: "error",
        text: e instanceof Error ? e.message : "Delete failed",
      });
    }
  };

  const viewMarkdown = async (id: number, filename: string) => {
    try {
      const data = await api.getUploadMarkdown(id);
      setMarkdownView({ id, filename, text: data.markdown });
    } catch (e) {
      setMessage({
        type: "error",
        text: e instanceof Error ? e.message : "Failed to load markdown snapshot",
      });
    }
  };

  // Auto-detect report type from filename
  const onFileChange = (f: File) => {
    setFile(f);
    const name = f.name.toLowerCase();
    if (name.includes("agenda")) {
      setReportType("agendas");
    } else if (name.includes("gestão") || name.includes("medico") || name.includes("médico") || name.includes("estrategia") || name.includes("estratégia")) {
      setReportType("strategy");
    } else if (name.includes("relatorio") || name.includes("diario") || name.includes("diário")) {
      setReportType("daily_activity");
    }
  };

  return (
    <div className={embedded ? "" : "p-8 max-w-4xl mx-auto"}>
      {!embedded && <h1 className="text-2xl font-bold text-slate-900 mb-2">Upload Reports</h1>}
      {!embedded && (
        <p className="text-slate-500 mb-6">
          Upload the daily Excel reports or the strategy workbook. The system auto-detects the report type
          from the filename. Specify the specialty from the email title, or "All" for the strategy workbook.
        </p>
      )}

      {/* Upload form */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Specialty / Scope
              </label>
              <select
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-trofa-700"
              >
                <option value="All">Todas as especialidades</option>
                <option value="Cardiologia">Cardiologia</option>
                <option value="Cardiologia Pediátrica">Cardiologia Pediátrica</option>
                <option value="Pneumologia">Pneumologia</option>
                <option value="Cirurgia Plástica">Cirurgia Plástica</option>
                <option value="Imunoalergologia">Imunoalergologia</option>
                <option value="Cirurgia Vascular">Cirurgia Vascular</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Report Type
              </label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-trofa-700"
              >
                <option value="">Auto-detect from filename</option>
                <option value="daily_activity">Daily Activity (Relatório Diário)</option>
                <option value="agendas">Agendas (Análise de Agendas)</option>
                <option value="strategy">Strategy 2027 (Gestão Médico a Médico)</option>
              </select>
            </div>
          </div>

          {/* File drop zone */}
          <div
            className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-trofa-700 transition-colors cursor-pointer"
            onClick={() => document.getElementById("file-input")?.click()}
            onDrop={(e) => {
              e.preventDefault();
              if (e.dataTransfer.files[0]) onFileChange(e.dataTransfer.files[0]);
            }}
            onDragOver={(e) => e.preventDefault()}
          >
            <input
              id="file-input"
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && onFileChange(e.target.files[0])}
            />
            {file ? (
              <div className="flex items-center justify-center gap-3 text-slate-700">
                <FileSpreadsheet className="w-8 h-8 text-green-600" />
                <div className="text-left">
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-slate-400">
                    {(file.size / 1024).toFixed(0)} KB
                  </p>
                </div>
              </div>
            ) : (
              <>
                <UploadCloud className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">
                  Click or drop your Excel file here
                </p>
                <p className="text-sm text-slate-400 mt-1">.xlsx files only</p>
              </>
            )}
          </div>

          {message && (
            <div
              className={`flex items-start gap-2 rounded-lg px-4 py-3 text-sm ${
                message.type === "success"
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : message.type === "warning"
                  ? "bg-amber-50 text-amber-700 border border-amber-200"
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}
            >
              {message.type === "success" ? (
                <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
              ) : message.type === "warning" ? (
                <TriangleAlert className="w-5 h-5 shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              )}
              <span className="flex-1">{message.text}</span>
            </div>
          )}

          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="w-full bg-trofa-700 hover:bg-trofa-700 text-white font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Parsing Excel…
              </>
            ) : (
              <>
                <UploadCloud className="w-5 h-5" />
                Upload & Parse
              </>
            )}
          </button>
        </div>
      </div>

      {/* Upload history */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">
          Upload History
        </h3>
        {uploads.length === 0 ? (
          <p className="text-slate-400 text-sm">No uploads yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Date</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Specialty</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Type</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">File</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-600">Records</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {uploads.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3 text-slate-700">
                      {new Date(u.uploaded_at).toLocaleString()}
                    </td>
                    <td className="py-2 px-3 text-slate-700">{u.specialty}</td>
                    <td className="py-2 px-3">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          u.report_type === "daily_activity"
                            ? "bg-blue-100 text-blue-700"
                            : u.report_type === "strategy"
                            ? "bg-green-100 text-green-700"
                            : "bg-purple-100 text-purple-700"
                        }`}
                      >
                        {u.report_type === "daily_activity" ? "Activity" : u.report_type === "strategy" ? "Strategy" : "Agendas"}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-slate-500 text-xs max-w-xs truncate">
                      {u.original_filename}
                    </td>
                    <td className="py-2 px-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {u.validation_warning && (
                          <span title={u.validation_warning} className="inline-flex items-center text-amber-600">
                            <TriangleAlert className="w-4 h-4" />
                          </span>
                        )}
                        <span className="text-slate-700">{u.row_count.toLocaleString()}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => viewMarkdown(u.id, u.original_filename)}
                          className="text-slate-400 hover:text-trofa-700"
                          title="View markdown snapshot"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(u.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Markdown snapshot modal */}
      {markdownView && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">
                Markdown Snapshot — {markdownView.filename}
              </h3>
              <button
                onClick={() => setMarkdownView(null)}
                className="text-slate-400 hover:text-slate-600"
              >
                ×
              </button>
            </div>
            <div className="p-4 overflow-auto text-xs">
              <pre className="whitespace-pre-wrap font-mono text-slate-700 bg-slate-50 p-3 rounded-lg border border-slate-200">
                {markdownView.text}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}